// ============================================================
// AttackFooter - Persistent action bar at the bottom of the
// AttackerPanel. Always visible when a weapon is selected,
// regardless of which panel tab is active.
//
// Contains: fire mode selector, ATTACK + ACTION buttons,
// Edit Slot/Reload, ammo display. ACTION opens the combat-action
// drum (v8.218.0) - the slot FREE occupied until then. FREE's
// countdown-skip is gone with it: the turn tracker and the
// roundCountdown house rule already govern when the per-attack
// countdown runs.
// ============================================================
import common from '../styles/common.module.css';
import clsx from '../utils/clsx';
import type { EconomyState } from '../engine/actions/economy';
import { describeEconomy } from '../engine/actions/economy';
import styles from '../styles/AttackerPanel.module.css';
import FireSelector from './FireSelector';
import MagazineControl from './MagazineControl';
import type { Slot } from '../types';

interface FireModeOption {
  key: string;
  label: string;
}

interface Props {
  slot: Slot;
  isMelee: boolean;
  fireMode: string;
  setFireMode: (mode: string) => void;
  fireModes: FireModeOption[];
  handleAttack: () => void;
  handleReload: () => void;
  isLocked: (key: string) => boolean;
  /**
   * The trigger's face - what firing it will DO (engine/actions/
   * chamber.ts ranks the claims). Defaults to the plain attack.
   */
  primaryLabel?: string;
  /** The action drum's open state - the footer button is its handle. */
  actionDrumOpen: boolean;
  toggleActionDrum: () => void;
  /**
   * Resolved house rule, exposed as a data attribute: the FREE button
   * used to be the E2E suite's only readout of this value.
   */
  roundCountdown?: boolean;
  /** The advisory action economy, or null when it is not being shown. */
  economy?: EconomyState | null;
  /** Zero the count by hand - the override for whatever the automation gets wrong. */
  onResetActionEconomy?: (() => void) | null;
}

export default function AttackFooter({
  slot,
  isMelee,
  fireMode,
  setFireMode,
  fireModes,
  handleAttack,
  handleReload,
  isLocked,
  primaryLabel = 'ATTACK',
  actionDrumOpen,
  toggleActionDrum,
  roundCountdown = false,
  economy = null,
  onResetActionEconomy = null,
}: Props) {
  const locked = isLocked('attack');
  return (
    <div className={styles.attackFooter} data-round-countdown={roundCountdown}>
      {economy && <EconomyPips economy={economy} onReset={onResetActionEconomy} />}
      {/* Fire selector - detents with a sliding indicator (v8.234.0) */}
      <FireSelector slot={slot} isMelee={isMelee} fireMode={fireMode} setFireMode={setFireMode} fireModes={fireModes} />
      {/* Action row: ATTACK + ACTION */}
      <div className={styles.footerActionRow}>
        <button
          className={common.btnActor}
          style={{ flex: 1, ...(locked ? { opacity: 0.4 } : {}) }}
          onClick={handleAttack}
          // The gun gesture (v8.236.0): right-clicking the trigger opens
          // the chamber selector, same as the ACTION button beside it.
          onContextMenu={(e) => {
            e.preventDefault();
            toggleActionDrum();
          }}
          aria-disabled={locked}
        >
          {primaryLabel} (Space)
          {locked ? <span style={{ fontSize: 9, marginLeft: 4 }}>&#x1F512;</span> : null}
        </button>
        <DrumButton open={actionDrumOpen} onToggle={toggleActionDrum} />
      </div>
      {/* The magazine gauge is reload button AND ammo readout since
          v8.235.0. (Edit Slot retired in v8.226.0 - the profile card
          and every armoury plate carry the pencil.) */}
      <MagazineControl slot={slot} isMelee={isMelee} onReload={handleReload} />
    </div>
  );
}

/**
 * The action economy (v8.309.0) - ADVISORY. Two pips a turn; a third and
 * beyond appear in amber rather than being refused, because tables that
 * allow more than a Full action exist and a simulator that argues with
 * the GM is worse than a quiet one. A click resets the count.
 */
function EconomyPips({ economy, onReset }: { economy: EconomyState; onReset: (() => void) | null }) {
  return (
    <button
      type="button"
      className={styles.economyRow}
      data-testid="action-economy"
      onClick={onReset ?? undefined}
      disabled={!onReset}
      aria-label="Action points spent this turn - click to reset"
      title={describeEconomy(economy) + (onReset ? ' Click to reset.' : '')}
    >
      {Array.from({ length: economy.total }, (_, i) => (
        <span
          key={i}
          className={clsx(styles.pip, i < economy.filled && styles.pipSpent)}
          data-testid={`economy-pip-${i}`}
          data-spent={i < economy.filled}
        />
      ))}
      {economy.over > 0 && (
        <span className={styles.pipOver} data-testid="economy-over">
          +{economy.over}
        </span>
      )}
    </button>
  );
}

/** ACTION - the drum's handle, lit while it is open. */
function DrumButton({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <button
      className={clsx(common.btnSmall, styles.drumHandle)}
      data-testid="action-drum-toggle"
      onClick={onToggle}
      aria-pressed={open}
      title="Combat actions - aim, feint, stances and more (F)"
    >
      ACTION
    </button>
  );
}
